// --------------------------------------------------------------
// Created On: 2022-01-14
// Author: Zachary Thomas
//
// Last Modified: 2026-09-30
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import { API, MAX_NAVBAR_ALERTS, MS_PER_SECOND } from "../../../constants/miscellaneous";
import useApi from "../../../hooks/useApi";
import apiRequest from "../../../utilities/api/apiRequest";
import { Link } from "react-router-dom";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import { setNotifications, deleteNotification } from "src/redux/features/notificationsSlice";
import useReduxSelector from "../../../hooks/useReduxSelector";
import styles from "./NotificationIndicator.module.scss";
import useLastActive from "../../../hooks/useLastActive";
import {
  CHECK_NOTIFICATIONS_MAX_INACTIVE_USER_SECONDS,
  CHECK_NOTIFICATIONS_POLLING_DELAY_SECONDS,
} from "src/constants/polling";

// Periodically updating list of notifications.
export default function NotificationIndicator(): Component {
  const [counter, setCounter] = useState<number>(0);
  const [dataPollCounter, setDataPollCounter] = useState<number>(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState<number>(0);
  const [shake, setShake] = useState<boolean>(false);
  const notifications = useReduxSelector((state) => state.notifications);
  const dispatch = useReduxDispatch();
  const userInactiveSeconds = useLastActive(CHECK_NOTIFICATIONS_POLLING_DELAY_SECONDS);

  // Get notification data from API.
  useApi(
    () => {
      return true;
    },
    {
      method: "GET",
      url: `${API}/alertmessage/all`,
      hideLog: true,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        dispatch(setNotifications(responseBody.alertMessages));
      } else {
        console.error("Internal server error. Unable to retrieve notifications.");
      }
    },
    [dataPollCounter]
  );

  // When the number of unread notification messages change, update the counter.
  useEffect(() => {
    const newUnreadNotificationCount = notifications.filter((notification) => notification.unread).length;
    if (newUnreadNotificationCount > unreadNotificationCount) {
      setUnreadNotificationCount(newUnreadNotificationCount);
      setShake(true);
    } else if (newUnreadNotificationCount < unreadNotificationCount) {
      setUnreadNotificationCount(newUnreadNotificationCount);
    } else if (newUnreadNotificationCount === 0) {
      setShake(false);
    }
  }, [JSON.stringify(notifications), unreadNotificationCount]);

  // Periodically poll notification data.
  useEffect(() => {
    const newTimerId = setTimeout(() => {
      setCounter((previousCounter) => previousCounter + 1);
      // Only poll data if the user is active.
      console.log(
        `Looking to see if notification data should be polled` +
          ` (inactive for ${userInactiveSeconds} seconds of ${CHECK_NOTIFICATIONS_MAX_INACTIVE_USER_SECONDS} second limit).`
      );
      if (userInactiveSeconds < CHECK_NOTIFICATIONS_MAX_INACTIVE_USER_SECONDS) {
        setDataPollCounter((prev) => prev + 1);
      } else {
        console.log("User is inactive. Notification data will not be polled.");
      }
    }, CHECK_NOTIFICATIONS_POLLING_DELAY_SECONDS * MS_PER_SECOND);

    return () => {
      clearTimeout(newTimerId);
    };
  }, [counter]);

  // Delete a notification message.
  async function deleteNotificationMessage(alertMessageId: number): Promise<void> {
    dispatch(deleteNotification(alertMessageId));
    const requestBody = { alertMessageIds: [alertMessageId] };
    void apiRequest(`${API}/alertmessage`, "DELETE", requestBody);
  }

  return (
    <div className={`${styles.container} dropdown`}>
      <button
        className={`${styles.btn} btn btn-primary ml-4 h-100`}
        type="button"
        onClick={() => setShake(false)}
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        data-test="notification-indicator"
      >
        {unreadNotificationCount > 0 && (
          <span className={styles.badge} data-test="notification-count">
            {unreadNotificationCount < 100 ? unreadNotificationCount : "99+"}
          </span>
        )}

        <i className={`${styles.bell} fa fa-fw fa-bell ${shake ? styles.shake : ""}`} />
      </button>

      <div className="dropdown-menu dropdown-menu-end" data-test="notifications-dropdown">
        {notifications
          .filter((notification) => notification.unread)
          .map((notification, i) => (
            <Fragment key={notification.alertMessageId}>
              {i < MAX_NAVBAR_ALERTS && (
                <div
                  className={`${styles.item} d-flex align-items-top dropdown-item pb-3`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p
                    className={`${styles.text} mb-0 ${
                      notification.urgency.toLowerCase() === "read" ? styles.read : ""
                    }`}
                  >
                    <span key={notification.alertMessageId}>{notification.message}</span>
                  </p>

                  <div className="mt-1 mx-4 d-inline-flex align-items-center">
                    {notification.alertUrl !== null && notification.alertUrl.length > 0 && (
                      <Link to={notification.alertUrl} className="router-link">
                        <i className="fa fa-fw fa-external-link" />
                      </Link>
                    )}
                  </div>

                  <button
                    className={`${styles.dismissNotificationButton} mt-1 d-inline-flex align-items-center`}
                    onClick={() => deleteNotificationMessage(notification.alertMessageId)}
                  >
                    <i className="fa fa-fw fa-times" data-test="dismiss-alert-button" />
                  </button>
                </div>
              )}
            </Fragment>
          ))}
        <Link to="/notification/overview" className="router-link">
          <button className={`${styles.viewAll} ${styles.item} dropdown-item`}>View All Notifications</button>
        </Link>
      </div>
    </div>
  );
}

interface AlertMessage {
  alertMessageId: number;
  alertCode: string;
  date: string;
  message: string;
  unread: boolean;
  urgency: string;
  alertUrl: string;
}

interface ResponseBody {
  alertMessages: AlertMessage[];
}
